/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useState} from "react"
import {Spin} from "antd"
import {PayPalScriptProvider} from "@paypal/react-paypal-js"

import {studentPaymentService} from "services"
import {handleError} from "helpers"
import {PaymentCreatedFromPlatform, PaymentProvider} from "types/student-account/student-payment"
import {GeneralPayment} from "types/student-account/ledger-accounts"
import {StudentCharge} from "types/student-account/student-ledger"
import {PaymentPlanItem} from "types/student-account/payment-plan"
import {PayPalCheckoutForm} from "./PaypalCheckoutForm"

type Props = {
    providerConfig
    studentId
    paymentAmount
    currency
    isCheckingOut: boolean
    selectedPayment?: GeneralPayment
    customNote: string
    payerFullName?: string
    selectedCharges?: StudentCharge[]
    selectedPaymentItems?: PaymentPlanItem[]
    academicYear?: number
}

export const PayPalPayment = (props: Props) => {
    const {
        providerConfig,
        studentId,
        paymentAmount,
        currency,
        isCheckingOut,
        selectedPayment,
        customNote,
        payerFullName,
        selectedCharges,
        selectedPaymentItems,
        academicYear
    } = props

    const [orderId, setOrderId] = useState<string | null>(null)
    const [studentPayment, setStudentPayment] = useState<any | null>(null)

    useEffect(() => {
        if (!studentId || !paymentAmount || !isCheckingOut) {
            setOrderId(null)
            setStudentPayment(null)
            return
        }

        ;(async function initPaypalOrder() {
            try {
                const {orderId, studentPayment} = await studentPaymentService.createPaypalOrder({
                    amountInCents: Math.floor(paymentAmount * 100),
                    currency,
                    studentProfileId: studentId,
                    generalPaymentId: selectedPayment?.generalPaymentId,
                    payerFullName,
                    academicYear,
                    createdFromPlatform: PaymentCreatedFromPlatform.EdularWeb,
                    paymentPlanItems: selectedPaymentItems?.length
                        ? selectedPaymentItems
                              .filter((item) => item.paymentAmount)
                              .map((item) => ({
                                  paymentPlanId: item.paymentPlanId,
                                  paymentPlanItemId: item.paymentPlanItemId,
                                  accountId: item.paymentAccountId,
                                  amount: paymentAmount
                              }))
                        : undefined,
                    notes: `${selectedPayment?.name || ""}${customNote ? `: ${customNote}` : ""}`
                })

                if (selectedCharges?.length) {
                    await studentPaymentService.assignPaymentCharges(
                        studentPayment.paymentId,
                        selectedCharges.map((charge) => ({
                            chargeId: charge.chargeId,
                            amount: charge.paymentAmount
                        }))
                    )
                }

                setOrderId(orderId)
                setStudentPayment(studentPayment)
            } catch (error) {
                handleError(error)
            }
        })()
    }, [
        studentId,
        isCheckingOut,
        paymentAmount,
        currency,
        selectedPayment,
        customNote,
        payerFullName,
        selectedCharges,
        selectedPaymentItems
    ])

    return (
        <>
            {!orderId || !studentPayment ? (
                <Spin />
            ) : (
                <PayPalScriptProvider
                    options={{
                        clientId: providerConfig.clientId,
                        currency: currency.toUpperCase()
                    }}>
                    <PayPalCheckoutForm orderId={orderId} studentPayment={studentPayment} />
                </PayPalScriptProvider>
            )}
        </>
    )
}
